import { HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { AlertService } from '@app/shared/services/alert.service';
import { AppSessionService } from '@app/shared/services/app-session.service';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import Swal from 'sweetalert2';
import { SessionStorageService } from '@app/shared/services/session-storage.service';
import { TokenService } from '@app/shared/services/token.service';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

interface SessionLogoutContext {
  reason: 'inactivity' | 'invalid_session' | 'manual_logout';
  tabId: string;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class UtilityService {

  @Output() updateLoggedInFlag: EventEmitter<any> = new EventEmitter();
  private idleStartSubscription?: Subscription;
  private timeoutWarningSubscription?: Subscription;
  private timeoutSubscription?: Subscription;
  private readonly SESSION_LOGOUT_CONTEXT_KEY = 'sessionLogoutContext';
  private readonly SESSION_LOGOUT_CONTEXT_TTL_MS = 5 * 60 * 1000;
  private readonly TAB_INSTANCE_ID_KEY = 'tabInstanceId';
  private readonly tabInstanceId: string;
  private seq = 0;

  constructor(
    private readonly idle: Idle,
    private readonly alertService: AlertService,
    private readonly sessionService: AppSessionService,
    private readonly storage: SessionStorageService,
    private readonly tokenService: TokenService,
    private readonly router: Router,
    private readonly dialog: MatDialog) {
    this.tabInstanceId = this.initializeTabInstanceId();
    globalThis.addEventListener('storage', this.handleCrossTabLogoutEvent);
    this.initiateIdleSubcription();
  }

  public initiateIdleSubcription() {
    //Setup ngIdle basic params, setup for 10 min
    this.idle.setIdle(600); // 10 min
    this.idle.setTimeout(20); // 20 sec
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.unsubscribeIdleEvents();

    //Fire when time is up
    this.idleStartSubscription = this.idle.onIdleStart.subscribe(() => {
      if (!this.isSessionActive()) {
        this.idle.stop();
        return;
      }

      console.log('Idle start event fired');
      const inactiveSession = 'Inactive session detected';
      const sessionLogout = 'Your session will logout in 20 seconds';
      this.alertService.buildDialog().fire({
        title: inactiveSession,
        text: sessionLogout,
        icon: this.alertService.WarningIcon,
        showCancelButton: false,
        confirmButtonText: 'Resume session',
        allowOutsideClick: false
      }).then((result) => {
        if (result.value) {
          this.idle.watch();
        }
      });
    });

    //Fire every second
    this.timeoutWarningSubscription = this.idle.onTimeoutWarning.subscribe((countdown) => {
      let inactiveSession: any = 'Your session will logout in $ seconds';
      inactiveSession = inactiveSession.replace('$', countdown);
      Swal.update({
        text: inactiveSession
      });
    });

    //Fire when time out and will be logged out
    this.timeoutSubscription = this.idle.onTimeout.subscribe(() => {
      this.signOut();
    });
  }

  private unsubscribeIdleEvents(): void {
    this.idleStartSubscription?.unsubscribe();
    this.timeoutWarningSubscription?.unsubscribe();
    this.timeoutSubscription?.unsubscribe();
  }

  public addToHttpParams(httpParams: HttpParams, value: any, key?: string): HttpParams {
    if (typeof value === 'object' && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }

  private addToHttpParamsRecursive(httpParams: HttpParams, value?: any, key?: string): HttpParams {
    if (value == null) {
      return httpParams;
    }

    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        (value).forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
      } else if (value instanceof Date) {
        httpParams = this.checkHttpParams(key, httpParams, value);
      } else {
        Object.keys(value).forEach(k => {
          const nestedKey = key == null ? k : `${key}.${k}`;
          httpParams = this.addToHttpParamsRecursive(httpParams, value[k], nestedKey);
        });
      }
    } else if (key == null) {
      throw new Error('key may not be null if value is not object or array');
    } else {
      httpParams = httpParams.append(key, value);
    }
    return httpParams;
  }
  /* istanbul ignore next */
  checkHttpParams(key, httpParams, value) {
    if (key == null) {
      throw new Error('key may not be null if value is Date');
    } else {
      httpParams = httpParams.append(key,
        (value).toISOString().substring(0, 10));
    }
    return httpParams;
  }

  // this method will clear user session and redirect to login page 
  signOut() {
    if (!this.isSessionActive()) {
      return; // Already logged out, do nothing
    }

    // Idle-timeout should always lead to invalid-session message in every tab.
    this.setSessionLogoutContext('inactivity');
    this.logoutFromSystem();
    this.router.navigate(['/']);
  }

  logoutFromSystem() {
    this.dialog.closeAll();
    // Stop any active idle timer to avoid background timers after logout
    this.idle.stop();
    this.unsubscribeIdleEvents();
    this.sessionService.removeBrowserSession();
    this.storage.set('IsUserLoggedIn', 'false');
    // remove user session from session storage
    this.tokenService.removeToken();
    this.tokenService.delete_cookieDomain();
    // set updateLoggedIn flag to false and emit to all subscribers
    this.updateLoggedInFlag.emit(false);
  }
  public removeSessionFlag(key: any): void {
    globalThis.sessionStorage.removeItem(key);
  }

  public getSessionFlag(key: string): string | null {
    return globalThis.sessionStorage.getItem(key);
  }

  public getTabInstanceId(): string {
    return this.tabInstanceId;
  }

  public setSessionLogoutContext(reason: 'inactivity' | 'invalid_session' | 'manual_logout'): void {
    // Persist logout reason so other tabs can show the right login message after redirect/refresh.
    const context: SessionLogoutContext = {
      reason,
      tabId: this.tabInstanceId,
      timestamp: Date.now()
    };
    localStorage.setItem(this.SESSION_LOGOUT_CONTEXT_KEY, JSON.stringify(context));
  }

  public getSessionLogoutContext(): SessionLogoutContext | null {
    const rawContext = localStorage.getItem(this.SESSION_LOGOUT_CONTEXT_KEY);
    if (!rawContext) {
      return null;
    }

    try {
      const context = JSON.parse(rawContext) as SessionLogoutContext;
      if (!context?.reason || !context?.tabId || !context?.timestamp) {
        this.clearSessionLogoutContext();
        return null;
      }

      if (Date.now() - context.timestamp > this.SESSION_LOGOUT_CONTEXT_TTL_MS) {
        this.clearSessionLogoutContext();
        return null;
      }

      return context;
    } catch {
      this.clearSessionLogoutContext();
      return null;
    }
  }

  public clearSessionLogoutContext(): void {
    localStorage.removeItem(this.SESSION_LOGOUT_CONTEXT_KEY);
  }

  private isSessionActive(): boolean {
    const token = this.tokenService.getToken();
    const hasValidToken = !!token && token !== 'null' && token !== 'undefined';
    return this.storage.get('IsUserLoggedIn') === 'true' || hasValidToken;
  }

  private readonly handleCrossTabLogoutEvent = (event: StorageEvent): void => {
    // When any tab logs out, force remaining tabs to logout and land on login.
    if (event.key !== this.SESSION_LOGOUT_CONTEXT_KEY || !event.newValue) {
      return;
    }

    const logoutContext = this.getSessionLogoutContext();
    if (!logoutContext || logoutContext.tabId === this.tabInstanceId) {
      return;
    }

    (globalThis as any)._sessionExpiredData = { isSessionExpired: true };
    this.logoutFromSystem();
    this.router.navigate(['/']);
  }

  private initializeTabInstanceId(): string {
    let generatedTabId: string;
    
    if (globalThis?.crypto?.randomUUID) {
      generatedTabId = globalThis.crypto.randomUUID();
    } else if (globalThis?.crypto?.getRandomValues) {
      const bytes = new Uint8Array(16);
      globalThis.crypto.getRandomValues(bytes);
      generatedTabId = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    } else {
      generatedTabId = this.createUniqueId();
    }

    globalThis.sessionStorage.setItem(this.TAB_INSTANCE_ID_KEY, generatedTabId);
    return generatedTabId;
  }
  
  private createUniqueId(): string {
    const now = Date.now().toString(36);
    this.seq = (this.seq + 1) % Number.MAX_SAFE_INTEGER;
    return `${now}-${this.seq.toString(36)}`;
  }
}
